import { useEffect, useMemo, useState } from "react";

export default function SupportPage() {
  const [verified, setVerified] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  // Dev helper: disable cooldown with ?nocooldown=1
  const disableCooldown = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("nocooldown") === "1";
  }, []);

  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("openForm") === "1") {
    setVerified(true);   
    setCooldown(false); 
  }
  }, []);

  useEffect(() => {
    if (disableCooldown) return;
    const ts = window.localStorage.getItem("gp_support_cooldown_ts");
    if (ts) {
      const mins = (Date.now() - Number(ts)) / 60000;
      if (mins < 2) setCooldown(true);
    }
  }, [disableCooldown]);

  useEffect(() => {
    window.onTurnstileVerify = (token) => {
      console.log("Turnstile token:", token);
      setVerified(true);
    };
    const id = "cf-turnstile-script"; 
    if (!document.getElementById(id)) {
      const s = document.createElement("script");
      s.id = id;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  useEffect(() => {
    function onMessage(e) {
      try {
        const d = e.data;
        const str = typeof d === "string" ? d.toLowerCase() : "";
        const submitted =
          (typeof d === "object" && d?.event && String(d.event).toLowerCase().includes("submit")) ||
          str.includes("formsubmitted") || str.includes("submitted") || str.includes("response");
        if (submitted && !disableCooldown) {
          window.localStorage.setItem("gp_support_cooldown_ts", String(Date.now()));
          setCooldown(true);
        }
      } catch {}
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [disableCooldown]);

  return (
    <div
      className="min-h-[100vh] flex flex-col"
      style={{ backgroundColor: "#e1f4e4", color: "#000" }}
    >
      <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "#e1f4e4",
        zIndex: -1,
        pointerEvents: "none",
      }}
    />
      <main className="flex-1 w-full" >
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-16">
          {/* Header */}
          <h1 className="text-3xl font-semibold tracking-tight text-black text-center">
            Contact &amp; Support
          </h1>
          <p className="mt-2 text-sm text-center text-black/80">
            Tell us what’s going on and we’ll reply within{" "}
            <span className="font-semibold">24–48 business hours</span>.
          </p>

          <div className="mt-8 grid gap-6">
            <div className="rounded-2xl p-6" style={{ backgroundColor: "transparent" }}>
              {!verified && !cooldown && (
                <div className="space-y-3">
                  <p className="text-sm text-black/80">
                    Verify you’re human to open the support form.
                  </p>
                  <div
                    className="cf-turnstile"
                    data-sitekey="0x4AAAAAABqsKHq4lMuGcXAl"  
                    data-callback="onTurnstileVerify"
                    data-theme="light"
                  />
                  <p className="text-xs text-black/60">
                    This prevents spam while keeping the form open to everyone (no sign-in required).
                  </p>
                </div>
              )}

              {cooldown && (
                <div
                  className="rounded-xl p-4"
                  style={{ backgroundColor: "rgba(253, 230, 138, 0.35)", border: "1px solid rgba(202,138,4,0.4)" }}
                >
                  <p className="text-sm text-black/85">
                    Thanks! You can re-open the form from this browser about{" "}
                    <span className="font-semibold">2 minutes</span> after submission.
                    {disableCooldown ? " (Cooldown disabled via ?nocooldown=1)" : ""}
                  </p>
                  <p className="text-xs text-black/60 mt-2">
                    Dev tip: run <code>localStorage.removeItem('gp_support_cooldown_ts')</code> in the console to clear it.
                  </p>
                </div>
              )}

              {verified && !cooldown && (
                <div className="mt-4">
                  <div
                    className="w-full rounded-2xl overflow-hidden"
                    style={{
                      height: "78vh",
                      backgroundColor: "#e1f4e4",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <iframe
                      title="GeoParking Support Request"
                      src="https://forms.office.com/Pages/ResponsePage.aspx?id=HpcpSB8wUUevSSWu4m0L7_pygkq7OmpKpXsPexQ2DdxUM1FRMTQ0M1ZKWUdUV09ZS0hFRjlTRjNOUy4u&embed=true"
                      width="100%"
                      height="100%"
                      allowFullScreen
                      style={{ border: 0, display: "block", background: "#e1f4e4" }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-black/80 text-center">
                    Trouble loading?{" "}
                    {/* <a
                      href="https://forms.office.com/Pages/ResponsePage.aspx?id=HpcpSB8wUUevSSWu4m0L7_pygkq7OmpKpXsPexQ2DdxUM1FRMTQ0M1ZKWUdUV09ZS0hFRjlTRjNOUy4u"
                      target="_blank"
                      rel="noreferrer"
                      className="underline text-black"
                    >
                      Open the form in a new tab
                    </a> */}
                     <a
                      href="/support?openForm=1"
                      target="_blank"
                      rel="noreferrer"
                      className="underline text-black"
                      >
                      Open the form in a new tab
                    </a>
                    .
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl p-6" style={{ backgroundColor: "transparent" }}>
              <h2 className="mt-2 text-lg font-semibold text-black text-center" style={{fontSize:"30px"}}>Other ways to reach us</h2>
              <p className="mt-2 text-sm text-black/80 text-center">
              <div style={{fontSize:"20px"}}>
                <span className="font-semibold">Email:</span>{" "}
                <a href="mailto:support@geoparking.com" className="underline text-black">
                  support@geoparking.com
                </a>
                {" · "}
                <a href="mailto:parking@geoparking.com" className="underline text-black">
                  parking@geoparking.com
                </a>
              </div>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
